  (function () {
    if (customElements.get('c-carousel')) return;

    /* Umbral en px que separa un clic de un arrastre. Por debajo de esto el
       gesto sigue siendo un clic normal sobre la card (link al producto, add
       to cart); a partir de aquí se toma como drag y el clic final se anula. */
    var DRAG_THRESHOLD = 5;

    function debounce(fn, wait) {
      var timer;
      return function () {
        var ctx = this;
        var args = arguments;
        clearTimeout(timer);
        timer = setTimeout(function () {
          fn.apply(ctx, args);
        }, wait);
      };
    }

    customElements.define(
      'c-carousel',
      class CCarousel extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-carousel-track]');
          if (!this.track) return;

          this.prevBtn = this.querySelector('[data-carousel-prev]');
          this.nextBtn = this.querySelector('[data-carousel-next]');
          this.dotsWrap = this.querySelector('[data-carousel-dots]');
          this.dotLabelTemplate = this.getAttribute('data-dot-label-template') || 'Page {n}';
          this.dots = [];

          this._dragging = false;
          this._dragMoved = false;
          this._pointerId = null;

          this._onScroll = debounce(this.updateState.bind(this), 100);
          this._onResize = debounce(this.refresh.bind(this), 150);
          this._onPrev = this.step.bind(this, -1);
          this._onNext = this.step.bind(this, 1);
          this._onPointerDown = this.onPointerDown.bind(this);
          this._onPointerMove = this.onPointerMove.bind(this);
          this._onPointerUp = this.onPointerUp.bind(this);
          this._onClickCapture = this.onClickCapture.bind(this);
          this._onDragStart = function (event) {
            event.preventDefault();
          };

          this.track.addEventListener('scroll', this._onScroll, { passive: true });
          if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
          if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
          window.addEventListener('resize', this._onResize);

          this.track.addEventListener('pointerdown', this._onPointerDown);
          this.track.addEventListener('pointermove', this._onPointerMove);
          this.track.addEventListener('pointerup', this._onPointerUp);
          this.track.addEventListener('pointercancel', this._onPointerUp);
          this.track.addEventListener('click', this._onClickCapture, true);
          /* Sin esto el navegador arranca su propio drag nativo de la imagen o
             del <a> de la card y el gesto muere a los pocos px. */
          this.track.addEventListener('dragstart', this._onDragStart);

          this.refresh();
        }

        disconnectedCallback() {
          if (!this.track) return;
          this.track.removeEventListener('scroll', this._onScroll);
          if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
          if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
          window.removeEventListener('resize', this._onResize);
          this.track.removeEventListener('pointerdown', this._onPointerDown);
          this.track.removeEventListener('pointermove', this._onPointerMove);
          this.track.removeEventListener('pointerup', this._onPointerUp);
          this.track.removeEventListener('pointercancel', this._onPointerUp);
          this.track.removeEventListener('click', this._onClickCapture, true);
          this.track.removeEventListener('dragstart', this._onDragStart);
        }

        /* ---------- Drag con mouse ----------
           El track es `overflow-x: auto`: con rueda/trackpad y con gesto táctil
           ya se desplaza solo, pero con el mouse no se podía agarrar. Esto
           mapea el movimiento del puntero a `scrollLeft` mientras el botón está
           pulsado — nada de traducir el track con transform, así flechas, dots
           y `scroll-snap` (que se apoyan en scrollLeft) siguen valiendo. */
        onPointerDown(event) {
          /* Antes de las guardas: cualquier pointerdown abre una interacción
             nueva y el flag del gesto anterior no puede sobrevivirle. Si se
             reseteara después, en una pantalla táctil un arrastre con mouse
             seguido de un toque con el dedo (que sale por el `return` de
             abajo) dejaría el flag activo y se comería ese tap. */
          this._dragMoved = false;

          /* Solo mouse/pen: en touch el scroll nativo ya trae inercia y
             secuestrarlo desde JS solo lo empeora. */
          if (event.pointerType === 'touch') return;
          if (event.button !== 0) return;
          if (!this.hasOverflow()) return;

          this._dragging = true;
          this._pointerId = event.pointerId;
          this._dragStartX = event.clientX;
          this._dragStartScroll = this.track.scrollLeft;
        }

        onPointerMove(event) {
          if (!this._dragging || event.pointerId !== this._pointerId) return;
          /* El puntero se soltó fuera del track (sin pointerup aquí): se cierra
             el gesto para no arrastrar con el botón ya libre. */
          if (event.buttons === 0) {
            this.endDrag(event.pointerId);
            return;
          }

          var dx = event.clientX - this._dragStartX;

          if (!this._dragMoved) {
            if (Math.abs(dx) < DRAG_THRESHOLD) return;
            this._dragMoved = true;
            /* `scroll-snap-type: x mandatory` pelea contra cada asignación de
               scrollLeft (reengancha en cada frame). Se apaga durante el gesto
               y se restaura al soltar — restaurarlo es justo lo que hace que
               enganche solo a la card más cercana. */
            this.track.style.scrollSnapType = 'none';
            this.track.classList.add('is-dragging');
            /* Capturar el puntero mantiene vivos los pointermove aunque el
               cursor salga del track. Va en try/catch: si el puntero ya no
               está activo el navegador tira NotFoundError y el gesto no debe
               morir por eso. */
            try {
              this.track.setPointerCapture(event.pointerId);
            } catch (e) {
              /* sin captura el drag sigue funcionando dentro del track */
            }
          }

          event.preventDefault();
          this.track.scrollLeft = this._dragStartScroll - dx;
        }

        onPointerUp(event) {
          this.endDrag(event ? event.pointerId : null);
        }

        endDrag(pointerId) {
          if (!this._dragging || (pointerId !== null && pointerId !== this._pointerId)) return;

          var id = this._pointerId;
          this._dragging = false;
          this._pointerId = null;
          /* `_dragMoved` NO se limpia aquí: lo necesita el click que llega
             inmediatamente después. Se resetea en el siguiente pointerdown. */
          if (!this._dragMoved) return;

          if (id !== null && this.track.hasPointerCapture && this.track.hasPointerCapture(id)) {
            this.track.releasePointerCapture(id);
          }
          this.track.classList.remove('is-dragging');
          this.track.style.scrollSnapType = '';
          this.updateState();
        }

        /* Todo arrastre termina en un `click` sobre la card que había debajo:
           si hubo gesto real se anula aquí, en fase de captura, para no navegar
           al producto ni disparar el add to cart. El flag se resetea en el
           pointerdown siguiente y no con un timer, así el orden
           pointerup → click no queda a merced del navegador. */
        onClickCapture(event) {
          if (!this._dragMoved) return;
          event.preventDefault();
          event.stopPropagation();
        }

        hasOverflow() {
          if (!this.track) return false;
          return this.track.scrollWidth - this.track.clientWidth > 1;
        }

        /* Público — llamado desde sections/c-collection-tabs.liquid al revelar
           un panel que estaba `hidden` (el track medía 0 mientras no era
           visible, igual que antes necesitaba `swiper.update()`). */
        refresh() {
          this.buildDots();
          this.updateState();
        }

        step(direction) {
          if (!this.track) return;
          this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: 'smooth' });
        }

        /* Tolerancia de 24px: en iOS el scrollWidth sale con decimales o con
           el gap final del track, y un ceil() exacto añadía un punto de más
           (2 slides -> 3 puntos) que no llevaba a ningún contenido nuevo. */
        pageCount() {
          if (!this.track || !this.track.clientWidth) return 1;
          return Math.max(1, Math.ceil((this.track.scrollWidth - 24) / this.track.clientWidth));
        }

        currentPage() {
          if (!this.track || !this.track.clientWidth) return 0;
          var page = Math.round(this.track.scrollLeft / this.track.clientWidth);
          return Math.min(Math.max(page, 0), this.pageCount() - 1);
        }

        buildDots() {
          if (!this.dotsWrap) return;
          var pages = this.pageCount();
          this.dotsWrap.innerHTML = '';
          this.dots = [];
          if (pages <= 1) return;

          for (var i = 0; i < pages; i++) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'c-product-carousel__dot';
            dot.setAttribute('aria-label', this.dotLabelTemplate.replace('{n}', i + 1));
            dot.addEventListener('click', this.goToPage.bind(this, i));
            this.dotsWrap.appendChild(dot);
            this.dots.push(dot);
          }
        }

        goToPage(index) {
          if (!this.track) return;
          this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
        }

        updateState() {
          if (!this.track) return;
          var maxScroll = this.track.scrollWidth - this.track.clientWidth;
          var hasOverflow = maxScroll > 1;

          /* Solo hay algo que arrastrar si el contenido desborda — de ahí sale
             el cursor `grab` (ver el CSS de cada sección que llama). */
          this.track.classList.toggle('is-draggable', hasOverflow);

          if (this.prevBtn) this.prevBtn.disabled = !hasOverflow || this.track.scrollLeft <= 1;
          if (this.nextBtn) this.nextBtn.disabled = !hasOverflow || this.track.scrollLeft >= maxScroll - 1;

          var current = this.currentPage();
          this.dots.forEach(function (dot, i) {
            var active = i === current;
            dot.classList.toggle('c-product-carousel__dot--active', active);
            if (active) {
              dot.setAttribute('aria-current', 'true');
            } else {
              dot.removeAttribute('aria-current');
            }
          });
        }
      }
    );
  })();
